import { instance } from 'api/api';

export interface TestRunOffer {
    description: string;
    suggested_prompt: string;
}

/** An additional resource the run left out; `message` arrives translated. */
export interface UnusedResource {
    key: string;
    label: string;
    reason: string;
    message: string;
}

export interface TestRunSuccess {
    status: 'success';
    narrative: string;
    significant: boolean;
    fallback_message: string;
    unused_resources?: UnusedResource[];
}

export interface TestRunFailure {
    status: 'failure';
    failure_message: string;
    // The dataset path always emits the key (null when no offer); the external path omits it.
    offer?: TestRunOffer | null;
    // Also folded into failure_message by the backend; kept for callers that need the structured list.
    unused_resources?: UnusedResource[];
}

export type TestRunResult = TestRunSuccess | TestRunFailure;

export interface TestRunScope {
    urlPrefix?: string;
    reportAiInsightId?: number;
    /** '' = a cleared picker. */
    primaryResource?: string;
    /** Picker keys (type:id). Undefined = not sent, so the backend runs against the stored rows; [] = none. */
    resources?: string[];
    /** Dataset Report only: the render context the canvas posts (measurement_time), so the preview anchors like the saved block. */
    properties?: Record<string, unknown>;
    /** Dataset Report only: the report's filters as the canvas serializes them. */
    filters?: string | null;
}

export const aiInsightAPI = {
    async testRun(
        elementId: number,
        prompt: string,
        isConditional: string,
        fallbackMessage: string,
        scope: TestRunScope = {},
    ): Promise<TestRunResult> {
        const {
            urlPrefix = '/data/editor/element/extreport',
            reportAiInsightId = 0,
            primaryResource = '',
            resources,
            properties,
            filters,
        } = scope;
        const response = await instance.post<{ status: string; data: TestRunResult }>(
            `${urlPrefix}/${elementId}/ai-insight-test-run`,
            {
                prompt,
                isConditional,
                fallbackMessage,
                report_ai_insight_id: reportAiInsightId,
                primary_resource: primaryResource,
                ...(resources !== undefined ? { resources } : {}),
                ...(properties !== undefined ? { properties: JSON.stringify(properties) } : {}),
                ...(filters ? { filters } : {}),
            },
        );

        return response.data.data;
    },
};
